<template>
  <b-modal
    id="external-assets-modal"
    :title="modalTitle"
    size="lg"
    ok-only
    :ok-disabled="!selectedAssets.length"
    @shown="onModalShown"
    @hidden="onModalHidden"
    @ok="handleOkClick"
  >
    <div class="external-assets-container">
      <div class="d-flex justify-content-between align-items-center mb-3">
        <div class="search-container flex-grow-1 mr-3">
          <div class="search-input-wrapper">
            <input
              v-model="searchQuery"
              type="text"
              class="search-input"
              placeholder="Search..."
              @keyup.enter="performSearch"
            >
            <svg
              width="13"
              height="13"
              class="search-icon"
            >
              <use href="/img/icons/sprite.svg#search-big" />
            </svg>
            <button
              v-if="searchQuery"
              class="clear-search-btn"
              @click="clearSearch"
            >
              <i class="cil-x" />
            </button>
          </div>
        </div>
      </div>

      <div v-if="assets.length || isLoading">
        <template v-if="isLoading">
          <div class="assets-grid">
            <div
              v-for="n in pageSize"
              :key="n"
              class="w-100 h-100"
              style="min-height: 167px;"
            >
              <b-skeleton
                width="100%"
                height="88%"
              />
              <b-skeleton />
            </div>
          </div>
        </template>

        <template v-else>
          <b-alert
            v-if="isSearchActive && assets.length && wasEnterPressed"
            show
            variant="info"
            class="mb-3"
          >
            Found {{ totalCount }} result{{ totalCount > 1 ? 's' : '' }} for "{{ searchQuery }}"
          </b-alert>

          <b-alert
            v-if="isSearchActive && !assets.length && !isLoading"
            show
            variant="warning"
            class="mb-3"
          >
            No results found for "{{ searchQuery }}"
          </b-alert>

          <div class="assets-grid">
            <GridItem
              v-for="asset in assets"
              :key="asset.id"
              :item="asset"
              :is-selected="isSelected(asset)"
              @click="onSelectAsset(asset)"
            />
          </div>
        </template>
      </div>

      <b-alert
        v-else
        show
        variant="warning"
      >
        No external assets available
      </b-alert>

      <div
        v-if="selectedAssets.length"
        class="selection-summary"
      >
        <span class="font-weight-bold">{{ selectedAssets.length }} asset{{ selectedAssets.length > 1 ? 's' : '' }} selected</span>
      </div>

      <div class="d-flex justify-content-center align-items-center">
        <b-pagination
          v-model="currentPage"
          :per-page="pageSize"
          :total-rows="totalCount"
          :disabled="isLoading"
          @change="onChangePage"
        />
      </div>
    </div>
  </b-modal>
</template>

<script>
import GridItem from '../file-browser/browser/GridItem.vue';

export default {
  name: 'ExternalAssetsView',

  components: { GridItem },

  props: {
    source: {
      type: String,
      default: 'bynder',
    }
  },

  data() {
    return {
      pageSize: 16,
      currentPage: 1,
      selectedAssets: [],
      searchQuery: '',
      wasEnterPressed: false,
      isLoading: false,
      assets: [],
      totalCount: 0,
    }
  },

  computed: {
    isSelected() {
      const selectedIds = this.selectedAssets.map(({ id }) => id);
      return asset => selectedIds.includes(asset.id);
    },

    isSearchActive() {
      return !!this.searchQuery.trim();
    },

    modalTitle() {
      // Maybe, we'll need to add Salsify later...
      const titles = {
        'bynder': 'Select Bynder Assets',
      };
      return titles[this.source] || 'Select Assets';
    }
  },

  methods: {
    onModalShown() {
      this.fetchAssets();
    },

    async fetchAssets() {
      this.isLoading = true;
      try {
        const params = {
          pageNumber: this.currentPage,
          pageSize: this.pageSize,
          isOnlyImages: true
        };

        if (this.searchQuery.trim()) {
          params.searchTerm = this.searchQuery.trim();
          params.searchType = 'search';
        }

        const { data } = await axios.get('/bynder/retrieve-assets', { params });

        if (data.status === 'error') {
          toastr.error(data.message || 'Failed to fetch assets');

          this.assets = [];
          this.totalCount = 0;

          return;
        }

        this.assets = data.items || [];
        this.totalCount = data.totalCount || 0;
      } catch (error) {
        console.error('Error fetching assets:', error);
        toastr.error('Failed to fetch assets from Bynder');

        this.assets = [];
        this.totalCount = 0;
      } finally {
        this.isLoading = false;
      }
    },

    performSearch() {
      if (this.searchQuery.trim()) {
        this.currentPage = 1;
        this.wasEnterPressed = true;
        this.fetchAssets();
      } else {
        this.clearSearch();
      }
    },

    clearSearch() {
      this.searchQuery = '';
      this.wasEnterPressed = false;
      this.currentPage = 1;
      this.fetchAssets();
    },

    onChangePage(page) {
      this.currentPage = page;
      this.fetchAssets();
    },

    onSelectAsset(asset) {
      const isSelected = this.isSelected(asset);

      if (isSelected) {
        this.selectedAssets = this.selectedAssets.filter(selected => selected.id !== asset.id);
      } else {
        this.selectedAssets.push(asset);
      }
    },

    handleOkClick() {
      this.$emit('onSelectedAssets', this.selectedAssets);
    },

    onModalHidden() {
      this.searchQuery = '';
      this.wasEnterPressed = false;
      this.currentPage = 1;
      this.selectedAssets = [];
      this.assets = [];
      this.totalCount = 0;
    }
  }
}
</script>

<style lang="scss" scoped>
.external-assets-container {
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;

  .search-container {
    margin-bottom: 0;
  }

  .search-input-wrapper {
    position: relative;
    width: 100%;
  }

  .search-input {
    width: 100%;
    height: 35px;
    background: white;
    border: 1px solid #b1b1b1;
    border-radius: 12px;
    padding: 0 35px 0 10px;
    outline: 0;
    font-family: "Nunito", sans-serif;
    font-style: normal;
    font-weight: 500;
    font-size: 14px;
    color: #464646;
  }

  .search-icon {
    position: absolute;
    right: 15px;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
  }

  .clear-search-btn {
    position: absolute;
    right: 35px;
    top: 50%;
    transform: translateY(-50%);
    background: none;
    border: none;
    cursor: pointer;
    color: #727272;
    padding: 0;
    font-size: 14px;

    &:hover {
      color: #333;
    }
  }

  .assets-grid {
    display: grid;
    grid-template-rows: auto;
    justify-items: center;
    grid-row-gap: 1rem;
    grid-column-gap: 1rem;
    margin-bottom: 1rem;
    grid-template-columns: repeat(4, 1fr);
  }

  .selection-summary {
    font-size: 14px;
    color: #464646;
    margin-bottom: 1rem;
  }
}
</style>
